'use client';
// MarketCard Component - Modern Design (Fully Responsive, No anonymous functions)
import { useMemo } from 'react';
import Link from 'next/link';
import { Box, Card, CardContent, Theme } from '@mui/material';

import {
  ChanceIndicator,
  MarketCardFooter,
  MarketCardHeader,
  MarketCardVariants,
} from '@/components';
import { useBookmarkToggle, useIsBookmarked } from '@/hooks';
import type { EventMarket, EventOutcome, EventResponse } from '@/types';
import {
  filterAndSortMarkets,
  findMarket,
  formatPercentage,
  getEventType,
  getMarketProbability,
  isEventLive,
  isMarketResolved,
  parseMarketOutcomes,
} from '@/utils';

import { ROUTES, UMA_RESOLUTION_STATUS } from '@/constants';

interface MarketCardProps {
  event: EventResponse;
  market: EventMarket;
  onTradeClick?: (marketId: string, outcomeId: string, isBuy: boolean) => void;
  activeCategorySlug?: string;
  selectedFilter?: string;
  alwaysShowBookmark?: boolean;
}

export function MarketCard({
  event,
  market,
  onTradeClick,
  activeCategorySlug,
  selectedFilter,
  alwaysShowBookmark = false,
}: MarketCardProps) {
  const outcomes = useMemo(() => {
    if (!market?.outcomes) return [];
    return parseMarketOutcomes(market.outcomes);
  }, [market?.outcomes]);

  const isBookmarked = useIsBookmarked(event.id);
  const handleBookmarkToggle = useBookmarkToggle();

  const isResolved = Boolean(
    isMarketResolved(market) || market.umaResolutionStatus === UMA_RESOLUTION_STATUS.RESOLVED,
  );

  const markets = event.markets || [];
  const sortedMarkets = useMemo(() => filterAndSortMarkets(markets), [markets]);
  const eventType = useMemo(() => getEventType(event), [event]);
  const chance = useMemo(() => getMarketProbability(market), [market]);
  const isGroupedEvent =
    (eventType === 'MULTI_OUTCOME' || eventType === 'MULTI_QUESTION') && sortedMarkets.length > 1;

  const rawTitle = isResolved ? event.markets[0].question : (event?.title ?? '-');

  const isCryptoMinuteFilter =
    activeCategorySlug === 'crypto' &&
    selectedFilter != null &&
    (selectedFilter?.toLowerCase().includes('5 min') ||
      selectedFilter?.toLowerCase().includes('15 min'));

  const marketTitle = isCryptoMinuteFilter
    ? `${rawTitle.split('-')[0].trim()} - ${selectedFilter}`
    : rawTitle;
  const totalVolume = event?.volume ?? 0;
  const image = event?.image ?? '';
  const footerEndDate = market.umaEndDate || market.closedTime || market.endDate;
  const isGame = !!event.gameId;
  const gameUrl =
    isGame && event?.gameId
      ? ROUTES.GAME_DETAIL(event?.homeTeam?.league || 'live', event?.id.toString())
      : undefined;

  const mlOutcomes = useMemo<EventOutcome[]>(() => {
    const moneylineMarket = findMarket(event, 'moneyline');
    if (!moneylineMarket) return [];
    try {
      return typeof moneylineMarket.outcomes === 'string'
        ? JSON.parse(moneylineMarket.outcomes)
        : (moneylineMarket.outcomes as EventOutcome[]) || [];
    } catch (e) {
      return [];
    }
  }, [event]);

  const isLive = useMemo(
    () => isEventLive(event, activeCategorySlug),
    [event, activeCategorySlug],
  );

  const team1 = {
    abbr: event.homeTeam?.abbreviation?.toUpperCase() || 'HME',
    name: event.homeTeam?.name || 'Team 1',
    color: event.homeTeam?.color || '#e8b046',
    logo: event.homeTeam?.logo,
    percentage: mlOutcomes[0]?.price != null ? formatPercentage(mlOutcomes[0].price, 0) : undefined,
  };

  const team2 = {
    abbr: event.awayTeam?.abbreviation?.toUpperCase() || 'AWY',
    name: event.awayTeam?.name || 'Team 2',
    color: event.awayTeam?.color || '#c24632',
    logo: event.awayTeam?.logo,
    percentage: mlOutcomes[1]?.price != null ? formatPercentage(mlOutcomes[1].price, 0) : undefined,
  };

  const handleBookmarkClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    handleBookmarkToggle(e, event, 'market');
  };

  return (
    <Link
      href={
        isGame
          ? gameUrl || '#'
          : isLive
            ? ROUTES.LIVE_MARKET_DETAILS(event?.slug || market?.id)
            : ROUTES.MARKET_DETAILS(event?.slug || market?.id)
      }
      style={{
        textDecoration: 'none',
        display: 'block',
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
      }}
    >
      <Card
        sx={{
          // Design spec (Porvenix homepage redesign — Landing.dc.html grid card):
          // radius 16px, 1px border, flat surface (no shadow), hover shifts
          // border-color + bg with a 140ms transition. Colors come from the
          // theme tokens (dark: bg #100f0d, border #201d19) so light mode is
          // unaffected — only the shape/interaction is aligned here.
          height: { xs: 235, sm: 220 },
          width: '100%',
          minWidth: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2, // 16px
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 'none',
          transition: 'border-color 140ms ease, background-color 140ms ease',
          cursor: 'pointer',
          '&:hover': {
            borderColor: (theme: Theme) =>
              theme.palette.mode === 'dark' ? '#35302a' : theme.palette.divider,
            bgcolor: (theme: Theme) =>
              theme.palette.mode === 'dark' ? '#141210' : theme.palette.action.hover,
          },
        }}
      >
        <CardContent
          sx={{
            // Design: 14px padding, tight vertical rhythm between header / body / footer.
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 0,
            p: '14px',
            overflow: 'hidden',
            '&:last-child': { pb: '10px' },
          }}
        >
          <MarketCardHeader
            title={marketTitle}
            imageUrl={image}
            sx={{ mb: 1 }}
            action={!isResolved && !isGroupedEvent && <ChanceIndicator percentage={chance} />}
            isGame={isGame}
            team1={team1}
            team2={team2}
            gameUrl={gameUrl}
            isResolved={isResolved}
          />
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: isResolved ? 'center' : 'flex-start',
              overflowY: 'auto',
              mt: 1.5,
              mb: isResolved ? 0 : 1.5,
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.1)', borderRadius: '10px' },
            }}
          >
            <MarketCardVariants
              eventType={eventType}
              event={event}
              market={market}
              outcomes={outcomes}
              onTradeClick={onTradeClick}
              sortedMarkets={sortedMarkets}
              isFullWidthActions={false}
            />
          </Box>
          <MarketCardFooter
            volume={totalVolume}
            isBookmarked={isBookmarked}
            onBookmarkClick={handleBookmarkClick}
            isResolved={isResolved}
            endDate={footerEndDate}
            isLive={isLive}
            alwaysShowBookmark={alwaysShowBookmark}
          />
        </CardContent>
      </Card>
    </Link>
  );
}

export default MarketCard;
